/**
 * LCFPIFinanceTab.js — Finance Tab
 * Wraps: Investor & DFI Workspace, DFI Matching, Portfolio Intelligence,
 *        EOI Tracker, Data Rooms
 */
import React, { useState, Suspense, lazy } from "react";
import { Users, Building2, BarChart2, Layers, Archive } from "lucide-react";
import { LCFPIErrorBoundary } from "@/components/lcfpi/LCFPIPageShell";

const LCFPIInvestorWorkspace = lazy(() => import("./LCFPIInvestorWorkspace"));
const LCFPIDFIMatching       = lazy(() => import("./LCFPIDFIMatching"));
const LCFPIPortfolio         = lazy(() => import("./LCFPIPortfolio"));
const LCFPIEOITracker        = lazy(() => import("./LCFPIEOITracker"));
const LCFPIDataRoom          = lazy(() => import("./LCFPIDataRoom"));

const SECTIONS = [
  { id: "investor",   label: "Investor & DFI",          icon: Users,      accent: "#059669", desc: "Opportunities · matching" },
  { id: "dfi",        label: "DFI Matching Engine",      icon: Building2,  accent: "#1e40af", desc: "Institutional alignment"  },
  { id: "portfolio",  label: "Portfolio Intelligence",   icon: BarChart2,  accent: "#7c3aed", desc: "Fund-level analytics"     },
  { id: "eoi-tracker",label: "EOI Tracker",              icon: Layers,     accent: "#0891b2", desc: "Engagement pipeline"      },
  { id: "data-room",  label: "Data Rooms",               icon: Archive,    accent: "#64748b", desc: "Institutional documents"  },
];

const Loader = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function LCFPIFinanceTab() {
  const [active, setActive] = useState("investor");
  const sec = SECTIONS.find(s => s.id === active) || SECTIONS[0];

  return (
    <div style={{ minHeight: "calc(100vh - 88px)", background: "#060d1a" }}>
      <div style={{ background: "#07101e", borderBottom: "1px solid #1e293b", padding: "0 24px", display: "flex", gap: 2, alignItems: "center", overflowX: "auto" }}>
        {SECTIONS.map(s => {
          const isActive = active === s.id; const Icon = s.icon;
          return (
            <button key={s.id} data-testid={`lcfpi-finance-sub-${s.id}`}
              onClick={() => setActive(s.id)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", background: isActive ? `${s.accent}18` : "transparent", border: "none", borderBottom: `2px solid ${isActive ? s.accent : "transparent"}`, color: isActive ? "#fff" : "#64748b", fontSize: 11, fontWeight: isActive ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap", transition: "all .15s" }}>
              <Icon size={11} color={isActive ? s.accent : "currentColor"} />
              {s.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: "12px 24px", borderBottom: "1px solid #0f172a", background: "#060d1a" }}>
        <p style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: ".08em" }}>
          <span style={{ color: sec.accent, fontWeight: 700 }}>Finance</span> › {sec.label} · {sec.desc}
        </p>
      </div>
      <LCFPIErrorBoundary>
        <Suspense fallback={<Loader />}>
          <div style={{ padding: "20px 24px" }}>
            {active === "investor"    && <LCFPIInvestorWorkspace />}
            {active === "dfi"         && <LCFPIDFIMatching />}
            {active === "portfolio"   && <LCFPIPortfolio />}
            {active === "eoi-tracker" && <LCFPIEOITracker />}
            {active === "data-room"   && <LCFPIDataRoom />}
          </div>
        </Suspense>
      </LCFPIErrorBoundary>
    </div>
  );
}
